import { useCallback, useEffect, useState } from 'react';
import { getWallets as getWalletsQuery, getWalletBalance } from '@/services/wallet';
import { TWalletData } from '../types/WalletData';
import { TGetWalletsRequestParams } from '@/services/types/WalletData';
import useAuth from '@/hooks/useAuth';
import useHomeContext from '@/hooks/useHomeContext';
import { getCards } from '@/services/card';
import { TSelectOptions } from '@/components/Select';
import { AccountTypeEnum } from '@/context/types/Account';
import { WalletTypeEnum } from '@/context/types/Wallet';
import { OwnedTypeEnum } from '@/services/types/Card';
import { isWalletEligible } from '@/utils/walletFilters';

export const useWallet = () => {
  const { business, accountId, accountType } = useAuth()!;
  const {
    setAllAccountsBalance,
    setAllAccounts,
    allAccounts,
    allAccountsBalance,
    wallets,
    setWallets,
    setIsLoading,
    setCards,
    defaultOption,
    setBankAccount,
    forceUpdateWalletsAndBalance,
    setForceUpdateWalletsAndBalance
  } = useHomeContext()!;
  const [allWallets, setAllWallets] = useState<TWalletData[]>([]);
  const [allOptions, setAllOptions] = useState<TSelectOptions[]>([]);

  const fetchWallets = useCallback(
    async (invalidateBalance = false) => {
      const getBalance = async (walletId: string) => {
        const res = await getWalletBalance(walletId);
        return res.data;
      };

      const getWallets = async (options: TGetWalletsRequestParams) => {
        const res = await getWalletsQuery(options);
        const resData = res.data;
        const walletData = await Promise.all(
          resData.map(async (wallet) => {
            const res = await getBalance(wallet.id);
            return {
              ...wallet,
              balance: res.balance
            };
          })
        );

        setWallets(walletData);
        if (invalidateBalance) {
          setAllAccountsBalance(undefined);
          setAllAccounts((prev) => ({ ...prev, balance: undefined }));
        }
        setIsLoading(false);
      };

      switch (accountType) {
        case AccountTypeEnum.BUSINESS: {
          if (business?.id) {
            getWallets({ businessId: business.id });
          }
          break;
        }

        case AccountTypeEnum.PERSONAL: {
          if (accountId) {
            getWallets({ accountId });
          }
        }
      }
    },
    [
      accountType,
      business,
      accountId,
      setWallets,
      setAllAccountsBalance,
      setAllAccounts,
      setIsLoading
    ]
  );

  useEffect(() => {
    fetchWallets();
  }, [fetchWallets]);

  useEffect(() => {
    if (forceUpdateWalletsAndBalance) {
      setForceUpdateWalletsAndBalance(false);
      fetchWallets(true);
    }
  }, [forceUpdateWalletsAndBalance, fetchWallets, setForceUpdateWalletsAndBalance]);

  useEffect(() => {
    const getAccountsBalance = (wallets: TWalletData[]) =>
      Number(
        wallets.reduce((prev, curr) => {
          return prev + Number(curr.balance);
        }, 0)
      );

    if (wallets.length && allAccountsBalance === undefined) {
      const queryCards = async () => {
        if (wallets.some((el) => el.hasCardCheck)) {
          return;
        }

        // Scoped by business/account (same as getWallets above) instead of looping
        // getCards({ walletId }) once per wallet - the backend already resolves all
        // cards across every wallet for that business/account in a single call.
        const cardsOptions =
          accountType === AccountTypeEnum.BUSINESS && business?.id
            ? { businessId: business.id }
            : accountId
              ? { accountId }
              : undefined;

        if (!cardsOptions) return;

        // The backend has no combined "all owned types" filter, so personal and team
        // cards are fetched separately (in parallel) and merged on the client. ownedType
        // is a mutually exclusive classification per card, so a plain concat is safe -
        // no card can come back from both calls.
        const [personalRes, teamRes] = await Promise.all([
          getCards({ ...cardsOptions, ownedType: OwnedTypeEnum.PERSONAL }),
          getCards({ ...cardsOptions, ownedType: OwnedTypeEnum.TEAM })
        ]);
        const mergedCards = [...personalRes.data, ...teamRes.data].sort(
          (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
        );

        setWallets((prev) => {
          return prev.map((el) => ({ ...el, hasCardCheck: true }));
        });

        setCards(
          mergedCards.map((card) => ({
            id: card.id,
            walletId: card.walletId,
            panEnd: card.panEnd,
            name: card.name
          }))
        );
      };

      setAllAccountsBalance(getAccountsBalance(wallets));
      queryCards();
    }

    if (allAccountsBalance !== undefined && allAccounts.balance === undefined) {
      setAllAccounts({ ...allAccounts, balance: allAccountsBalance });
    }
  }, [wallets, allAccountsBalance]);

  useEffect(() => {
    if (wallets.length === 1) {
      setAllWallets(wallets);
      setAllOptions([
        ...wallets.map((account) => ({
          label: account.name,
          labelDescription: account.balance != null ? account.balance.toFixed(2) : undefined,
          value: account.name
        }))
      ]);
    } else {
      setAllWallets([allAccounts, ...wallets]);
      setAllOptions([
        defaultOption,
        ...wallets.map((account) => ({
          label: account.name,
          labelDescription: account.balance != null ? account.balance.toFixed(2) : undefined,
          value: account.name
        }))
      ]);
    }
  }, [wallets, allAccounts]);

  useEffect(() => {
    if (allWallets.length) {
      setBankAccount((current) => current ?? allWallets[0]);
    }
  }, [allWallets]);

  useEffect(() => {
    setBankAccount((current) => {
      if (!current) return current;
      if (current.id === allAccounts.id) {
        return allAccounts.balance === undefined ? current : allAccounts;
      }
      return wallets.find((wallet) => wallet.id === current.id) ?? current;
    });
  }, [wallets, allAccounts]);

  const findWalletById = (walletId: string): TWalletData | undefined => {
    return wallets.find((wallet) => wallet.id === walletId);
  };

  const isWalletEligibleByIdAndType = (
    walletId: string,
    eligibleWalletTypes: WalletTypeEnum[]
  ): boolean => isWalletEligible(findWalletById(walletId), eligibleWalletTypes);

  return { allWallets, allOptions, findWalletById, isWalletEligibleByIdAndType };
};
